/** How the design studio writes numbers ([design-studio §11.1], [ux §2]). */
import type { KindEstimate, ModelParam, ModelValues } from "../api/types";
import { formatApprox } from "../ui/format";
import { humanise } from "../viewer/math";

/** "6,000" */
export function credits(n: number): string {
  return n.toLocaleString("en-US");
}

/** The time of an instruction's estimate: under a minute as `formatApprox`
 *  writes it (without "about "), otherwise in half minutes (90 s is
 *  "1.5 min", 150 s "2.5 min"). Only the two estimate lines of an instruction
 *  use it: whole minutes would hide the difference between a first step and
 *  a later one. */
export function halfMinutes(seconds: number): string {
  if (seconds < 60) return formatApprox(seconds).replace(/^about /, "");
  return `${Math.ceil(seconds / 30) / 2} min`;
}

/** What an instruction usually costs: the range the API measured, or one
 *  number when its two ends are the same. */
export function creditRange(e: {
  credits_low: number;
  credits_high: number;
}): string {
  return e.credits_low === e.credits_high
    ? credits(e.credits_low)
    : `${credits(e.credits_low)} to ${credits(e.credits_high)}`;
}

/** The estimate line of an instruction; `hold` is what would be held now. */
export function estimateLine(e: KindEstimate, hold: number): string {
  return `About ${creditRange(e)} credits, about ${halfMinutes(e.seconds)}. Up to ${credits(hold)} are held while it works; what is not used is released.`;
}

/** A plain number as the sliders write theirs: at most one decimal. */
function number(v: number): string {
  return v.toLocaleString("en-US", { maximumFractionDigits: 1 });
}

/** A length in metres, shown in centimetres: 0.26 is "26 cm". */
export function cm(v: number): string {
  return `${number(v * 100)} cm`;
}

/** The unit a setting is shown with: lengths are stored in metres and shown
 *  in centimetres, angles carry the degree sign. */
export function shownUnit(unit: string): string {
  if (unit === "m") return "cm";
  if (unit === "deg") return "°";
  return unit;
}

/** The label of a setting: its own, else its humanised name. */
export function paramLabel(param: Pick<ModelParam, "name" | "label">): string {
  return param.label || humanise(param.name);
}

/** One value of a setting as the history writes it. `param` is the entry of
 *  the current step with that name; without one the bare value is written. */
export function valueText(
  value: ModelValues[string],
  param: ModelParam | undefined,
): string {
  if (typeof value === "boolean") return value ? "on" : "off";
  if (typeof value === "string") return humanise(value);
  if (param?.kind === "param") {
    return number(param.unit === "m" ? value * 100 : value);
  }
  // No setting of that name any more: the number as it was stored.
  return value.toLocaleString("en-US", { maximumFractionDigits: 3 });
}

/** "Height 26 → 30 cm, Cap loop → flat": every value of `after` that differs
 *  from `before`, by the labels and units of `params` (the current step's).
 *  A choice and a switch have no old value worth reading: only the new one
 *  is written. */
export function tunedText(
  before: ModelValues | null,
  after: ModelValues | null,
  params: ModelParam[],
): string {
  const parts: string[] = [];
  for (const [name, value] of Object.entries(after ?? {})) {
    const old = before?.[name];
    if (old === value) continue;
    const param = params.find((p) => p.name === name);
    const label = param ? paramLabel(param) : humanise(name);
    if (typeof value === "number") {
      const unit = param?.kind === "param" ? shownUnit(param.unit) : "";
      const suffix = unit === "°" ? unit : unit ? ` ${unit}` : "";
      const from = typeof old === "number" ? `${valueText(old, param)} → ` : "";
      parts.push(`${label} ${from}${valueText(value, param)}${suffix}`);
    } else {
      parts.push(`${label} → ${valueText(value, param)}`);
    }
  }
  return parts.join(", ");
}
